import React from 'react'
import hoistNonReactStatic from 'hoist-non-react-statics'
import ssrPrepass from 'react-ssr-prepass'
import {dehydrate, Hydrate, QueryClient, QueryClientProvider} from '@tanstack/react-query'
import {FetchStrategy} from '@salesforce/pwa-kit-react-sdk/ssr/universal/components/fetch-strategy'
import {IFRAME_HOST_ALLOW_LIST} from '@salesforce/commerce-sdk-react/constant'
import logger from '../utils/logger-instance'

const STATE_KEY = '__reactQuery'
const passthrough = (input) => input

/**
 * Tells whether a query failed in a way that leaves the page incomplete. A 404 is a definitive
 * not-found answer and does not count.
 *
 * @param {Object} query A query from the React Query cache.
 * @returns {boolean}
 */
const isFailedQuery = (query) =>
    query.state.status === 'error' &&
    (query.state.error?.response?.status ?? query.state.error?.status) !== 404

/**
 * Names a query for logging without leaking ids from the rest of its key.
 *
 * @param {Object} query A query from the React Query cache.
 * @returns {string|undefined}
 */
const getQueryName = (query) =>
    query.meta?.displayName ??
    (typeof query.queryKey[0] === 'string' ? query.queryKey[0] : undefined)

/**
 * Runs the prepass and collects the options of queries rendered as enabled. The prepass never
 * subscribes observers, so a cached query keeps the options it was created with.
 *
 * @param {Object} queryClient The query client used by the rendered app.
 * @param {React.ReactElement} appJSX The app to render.
 * @returns {Promise<Map<string, Object>>} The defaulted options by query hash.
 */
const collectEnabledQueries = async (queryClient, appJSX) => {
    const enabledQueries = new Map()
    const {defaultQueryOptions} = queryClient
    const isOwnMethod = Object.hasOwn(queryClient, 'defaultQueryOptions')
    // every query hook defaults its options here
    queryClient.defaultQueryOptions = (options) => {
        const defaultedOptions = defaultQueryOptions.call(queryClient, options)
        // keep the options of a hook with a query function over one only reading the cache
        if (
            defaultedOptions.enabled !== false &&
            (defaultedOptions.queryFn || !enabledQueries.has(defaultedOptions.queryHash))
        ) {
            enabledQueries.set(defaultedOptions.queryHash, defaultedOptions)
        }
        return defaultedOptions
    }
    try {
        await ssrPrepass(appJSX)
    } finally {
        if (isOwnMethod) {
            queryClient.defaultQueryOptions = defaultQueryOptions
        } else {
            delete queryClient.defaultQueryOptions
        }
    }
    return enabledQueries
}

/**
 * A HoC for adding React Query support to your application and handling waterfall queries.
 *
 * based on withReactQuery from pwa-kit-react-sdk
 *
 * @param {React.ReactElement} Wrapped The component to be wrapped
 * @param {Object} options
 * @param {Object} options.queryClientConfig The react query client configuration object to be used.
 *
 * @returns {React.ReactElement}
 */
export const withWaterfallReactQuery = (Wrapped, options = {}) => {
    const isServerSide = typeof window === 'undefined'
    /* istanbul ignore next */
    const wrappedComponentName = Wrapped.displayName || Wrapped.name
    const queryClientConfig = options.queryClientConfig
    const beforeHydrate = options.beforeHydrate || passthrough
    const maxWaterfallDepth = options.maxWaterfallDepth || 5
    const warnWaterfallTime = options.warnWaterfallTime || 2500
    const warnPrepassTime = options.warnPrepassTime || 250
    const warnQueryTime = options.warnQueryTime || 1000

    /**
     * @private
     */
    class WithWaterfallReactQuery extends FetchStrategy {
        render() {
            let preloadedState = {}

            this.props.locals.__queryClient =
                this.props.locals.__queryClient || new QueryClient(queryClientConfig)

            if (!isServerSide) {
                try {
                    preloadedState = beforeHydrate(window.__PRELOADED_STATE__?.[STATE_KEY] || {})
                } catch (e) {
                    logger.error('Client `beforeHydrate` failed', {
                        namespace: 'with-react-query.render',
                        additionalProperties: {error: e}
                    })
                }
            }

            return (
                <QueryClientProvider client={this.props.locals.__queryClient}>
                    <Hydrate state={preloadedState}>
                        <Wrapped {...this.props} />
                    </Hydrate>
                </QueryClientProvider>
            )
        }

        /**
         * @private
         */
        static async doInitAppState({req, res, appJSX, route}) {
            // skip server-side rendering if the referer is in the iframe allow list (i.e. preview mode)
            // as we don't have access to the modified shopper context and will prerender a different page
            const isPreview =
                req.headers.referer &&
                URL.canParse(req.headers.referer) &&
                IFRAME_HOST_ALLOW_LIST.includes(new URL(req.headers.referer).origin)

            const skipQueries =
                // forced client-side rendering
                '__client_only' in req.query ||
                // if the limitOutput flag is set, we should not fetch any data to avoid excessive response sizes
                res.locals?.limitOutput ||
                isPreview

            let enabledQueries = new Map()
            const isActiveQuery = (q) =>
                q.state.status === 'loading' &&
                enabledQueries.has(q.queryHash) &&
                (!skipQueries || enabledQueries.get(q.queryHash).forceOnServer)

            const queryClient = (res.locals.__queryClient =
                res.locals.__queryClient || new QueryClient(queryClientConfig))

            const queryCache = queryClient.getQueryCache()

            const __startWaterfall = Date.now()
            let rounds = 0
            let prepassMs = 0
            let fetchMs = 0
            let slowestQuery
            let slowestMs = 0

            // Fetch all pending queries in a waterfall pattern
            for (let depth = 0; depth < maxWaterfallDepth; depth++) {
                // Use `ssrPrepass` to collect all uses of `useQuery`.
                const __startPrepass = Date.now()
                enabledQueries = await collectEnabledQueries(queryClient, appJSX)
                const __endPrepass = Date.now()
                rounds++
                prepassMs += __endPrepass - __startPrepass
                if (__endPrepass - __startPrepass > warnPrepassTime) {
                    console.warn(
                        `Slow prepass during iteration ${depth + 1} - ${
                            __endPrepass - __startPrepass
                        }ms`
                    )
                }

                // exit in case we are redirected as the result doesn't matter in that case
                if (appJSX?.props?.routerContext?.url) {
                    return
                }

                const queries = queryCache.getAll().filter(isActiveQuery)

                // short-circuit if there are no more pending queries
                if (!queries.length) {
                    break
                }

                // queries start together, so the slowest one spans the round
                let roundMs = 0
                await Promise.all(
                    queries.map((q) => {
                        const __startQuery = Date.now()
                        // If there's an error in this fetch, react-query will log the error
                        return q
                            .fetch(enabledQueries.get(q.queryHash))
                            .catch(() => {
                                // On our end, simply catch any error and move on to the next query
                            })
                            .finally(() => {
                                const __endQuery = Date.now()
                                const queryMs = __endQuery - __startQuery
                                roundMs = Math.max(roundMs, queryMs)
                                if (queryMs > slowestMs) {
                                    slowestMs = queryMs
                                    slowestQuery = getQueryName(q)
                                }
                                if (__endQuery - __startQuery > warnQueryTime) {
                                    console.warn(
                                        `Slow query ${q.queryHash} during iteration ${
                                            depth + 1
                                        } - ${__endQuery - __startQuery}ms`
                                    )
                                }
                            })
                    })
                )
                fetchMs += roundMs
            }

            const __endWaterfall = Date.now()
            if (__endWaterfall - __startWaterfall > warnWaterfallTime) {
                console.warn(
                    `Slow fetching all pending queries - ${__endWaterfall - __startWaterfall}ms`
                )
            }

            const hasPendingQueries = queryCache.getAll().some(isActiveQuery)
            if (hasPendingQueries) {
                console.error(
                    `Failed to fetch all pending queries in ${maxWaterfallDepth} iterations`
                )
            }

            const hasFailedQueries = queryCache.getAll().some(isFailedQuery)

            res.locals.ssrMetrics = {
                route: route?.pathPattern ?? route?.path,
                pageType: route?.pageType,
                // config entries resolved by AppConfig.restore
                site: res.locals.site?.id ?? null,
                locale: res.locals.locale?.id ?? null,
                rounds,
                prepassMs,
                fetchMs,
                totalMs: __endWaterfall - __startWaterfall,
                slowestQuery,
                slowestMs,
                pending: hasPendingQueries,
                failed: hasFailedQueries,
                preview: Boolean(isPreview),
                limitOutput: Boolean(res.locals.limitOutput)
            }

            return {[STATE_KEY]: dehydrate(queryClient)}
        }

        /**
         * @private
         */
        static getInitializers() {
            return [WithWaterfallReactQuery.doInitAppState, ...(Wrapped.getInitializers?.() ?? [])]
        }

        /**
         * @private
         */
        static getHOCsInUse() {
            return [withWaterfallReactQuery, ...(Wrapped.getHOCsInUse?.() ?? [])]
        }
    }

    WithWaterfallReactQuery.displayName = `withWaterfallReactQuery(${wrappedComponentName})`

    const exclude = {
        doInitAppState: true,
        getInitializers: true,
        initAppState: true,
        getHOCsInUse: true
    }
    hoistNonReactStatic(WithWaterfallReactQuery, Wrapped, exclude)

    return WithWaterfallReactQuery
}
